import React from 'react';
import AuditLabel from './AuditLabel';
import { calculateSizeTag } from '../utils/dogUtils';
import { formatDateTime, formatTime } from '../utils/dateUtils';
import { GROOMING_SERVICE_LABEL } from '../utils/groomingUtils';

const STATUS_LABEL = {
    scheduled: 'Programado',
    'in-progress': 'En proceso',
    completed: 'Completado',
    cancelled: 'Cancelado',
};

const PAYMENT_LABEL = {
    pending: 'Pendiente',
    paid: 'Pagado',
    membership: 'Membresía',
    'bath-package': 'Paquete Baño',
};

const GroomingBookingCard = ({ booking, onEdit, onDelete, onStatusUpdate, onPaymentUpdate }) => {
    const getStatusBadgeClass = (status) => {
        const map = { scheduled: 'scheduled', 'in-progress': 'in-progress', completed: 'completed', cancelled: 'cancelled' };
        return `status-badge ${map[status] || ''}`;
    };

    const getPaymentBadgeClass = (ps) => {
        const map = { pending: 'gb-pay--pending', paid: 'gb-pay--paid', membership: 'gb-pay--membership', 'bath-package': 'gb-pay--bath' };
        return `gb-pay-badge ${map[ps] || 'gb-pay--pending'}`;
    };

    const formatServices = (services) => {
        if (!services || services.length === 0) return 'Sin servicios seleccionados';
        return services.map(s => GROOMING_SERVICE_LABEL[s] ?? s).join(', ');
    };

    const canDelete = (status) => status === 'scheduled' || status === 'cancelled';

    const dogName = booking.dog?.name || booking.manualDogName || 'Perro desconocido';
    const ownerNames = booking.dog?.owners?.map(owner => owner.name).join(', ') || booking.manualOwnerName || '';
    const ps = booking.paymentStatus || 'pending';

    return (
        <div className="grooming-booking-card">
            <div className="booking-header">
                <h3>🐕 {dogName}</h3>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className={getPaymentBadgeClass(ps)}>{PAYMENT_LABEL[ps]}</span>
                    <span className={getStatusBadgeClass(booking.status)}>
                        {STATUS_LABEL[booking.status] || booking.status}
                    </span>
                </div>
            </div>

            <div className="booking-info">
                <p className="dog-name">Perro: {dogName}</p>
                {ownerNames && <p className="owner-names">Propietario(s): {ownerNames}</p>}
                <p className="appointment-date">Fecha: {formatDateTime(booking.appointmentDate)}</p>
                <p className="appointment-time">Hora entrada: {formatTime(booking.appointmentDate)}</p>
                {booking.departureTime && (
                    <p className="departure-time">
                        <span className="departure-time-badge">🕐 Salida: {booking.departureTime}</span>
                    </p>
                )}
                <p className="owner-phone">Teléfono: {booking.ownerPhone}</p>
                <p className="dog-weight">Peso: {booking.weight} kg</p>
                {booking.weight && (() => {
                    const sizeTag = calculateSizeTag(booking.weight);
                    return (
                        <p className="dog-size">
                            <strong>Size:</strong>
                            <span className="size-tag-inline">
                                <span className="size-tag-badge-small" title={sizeTag.description}>
                                    {sizeTag.tag}
                                </span>
                                <span className="size-tag-label-inline">{sizeTag.label}</span>
                            </span>
                        </p>
                    );
                })()}
            </div>

            <div className="booking-services">
                <strong>Servicios:</strong> {formatServices(booking.groomingServices)}
            </div>

            {booking.notes && (
                <div className="booking-notes">
                    <strong>Notas:</strong> {booking.notes}
                </div>
            )}

            {booking.groomingNotes && (
                <div className="grooming-notes">
                    <strong>Notas de estética:</strong> {booking.groomingNotes}
                </div>
            )}

            {booking.completedAt && (
                <div className="completed-info">
                    <strong>Completado:</strong> {formatDateTime(booking.completedAt)}
                </div>
            )}

            {/* Inline status actions */}
            {onStatusUpdate && booking.status !== 'cancelled' && booking.status !== 'completed' && (
                <div className="gb-inline-status">
                    <span className="gb-inline-label">Estado:</span>
                    {booking.status === 'scheduled' && (
                        <button className="gb-status-btn gb-status-btn--progress" onClick={() => onStatusUpdate(booking._id, 'in-progress')}>
                            En proceso
                        </button>
                    )}
                    {(booking.status === 'scheduled' || booking.status === 'in-progress') && (
                        <button className="gb-status-btn gb-status-btn--complete" onClick={() => onStatusUpdate(booking._id, 'completed')}>
                            Completar
                        </button>
                    )}
                    {(booking.status === 'scheduled' || booking.status === 'in-progress') && (
                        <button className="gb-status-btn gb-status-btn--cancel" onClick={() => onStatusUpdate(booking._id, 'cancelled')}>
                            Cancelar
                        </button>
                    )}
                </div>
            )}

            {/* Inline payment toggle */}
            {onPaymentUpdate && (
                <div className="gb-inline-payment">
                    <span className="gb-inline-label">Pago:</span>
                    {(['pending', 'paid', 'membership', 'bath-package']).map(opt => (
                        <button
                            key={opt}
                            className={`gb-pay-btn gb-pay-btn--${opt === 'bath-package' ? 'bath' : opt}${ps === opt ? ' gb-pay-btn--active' : ''}`}
                            disabled={ps === opt}
                            onClick={() => onPaymentUpdate(booking._id, opt)}
                        >
                            {PAYMENT_LABEL[opt]}
                        </button>
                    ))}
                </div>
            )}

            {/* Audit Trail */}
            <AuditLabel
                createdBy={booking.createdBy}
                updatedBy={booking.updatedBy}
                createdAt={booking.createdAt}
                updatedAt={booking.updatedAt}
                size="small"
            />

            <div className="card-actions">
                {onEdit && (
                    <button
                        onClick={() => onEdit(booking)}
                        className="edit-btn"
                        title="Ver / editar reserva"
                    >
                        Ver
                    </button>
                )}

                {canDelete(booking.status) && onDelete && (
                    <button
                        onClick={() => onDelete(booking._id)}
                        className="delete-btn"
                        title="Eliminar reserva"
                    >
                        Eliminar
                    </button>
                )}
            </div>
        </div>
    );
};

export default GroomingBookingCard;
